import { cn } from "@/lib/utils";
import { SetupSection } from "./ImagesStep";
import {
  ALL_HOLES,
  describeHoleChoice,
  setHoleMode,
  toggleHole,
  type HoleMode,
  type PlacementSetup,
} from "./campaignDraft";

const MODES: { mode: HoleMode; label: string }[] = [
  { mode: "ALL", label: "All 18" },
  { mode: "FRONT", label: "Front 9" },
  { mode: "BACK", label: "Back 9" },
  { mode: "CHOOSE", label: "Choose holes" },
];

interface HolesQuestionProps {
  setup: PlacementSetup;
  onChange: (setup: PlacementSetup) => void;
}

/** Question 1, only for placements whose metadata.isHoleEnabled is true. */
export function HolesQuestion({ setup, onChange }: HolesQuestionProps) {
  return (
    <SetupSection number={1} title="Which holes does it show on?">
      <div
        role="radiogroup"
        aria-label="Which holes"
        className="inline-flex flex-wrap gap-0.5 self-start rounded-lg bg-muted p-0.5"
      >
        {MODES.map(({ mode, label }) => {
          const on = setup.holeMode === mode;
          return (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(setHoleMode(setup, mode))}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-semibold transition-colors",
                on
                  ? "bg-surface text-ink shadow-sm"
                  : "text-text-muted hover:text-ink",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      {setup.holeMode === "CHOOSE" && (
        <div className="grid max-w-105 grid-cols-9 gap-1.5">
          {ALL_HOLES.map((hole) => {
            const on = setup.holes.includes(hole);
            return (
              <button
                key={hole}
                type="button"
                aria-pressed={on}
                aria-label={`Hole ${hole}`}
                onClick={() => onChange(toggleHole(setup, hole))}
                className={cn(
                  "flex size-9 items-center justify-center rounded-full border-[1.5px] text-xs font-semibold transition-colors",
                  on
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-text-muted hover:border-ink/40",
                )}
              >
                {hole}
              </button>
            );
          })}
        </div>
      )}

      <p className="text-sm text-text-muted">
        {setup.holes.length === 0
          ? "Pick at least one hole."
          : `Shows on ${describeHoleChoice(setup)}. Holes outside this never show this placement.`}
      </p>
    </SetupSection>
  );
}
